import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useRoom } from '../room/RoomProvider';

interface ChatPanelProps {
  dockInputOnMobile?: boolean;
}

export function ChatPanel({ dockInputOnMobile = false }: ChatPanelProps) {
  const { socket, chatMessages, chatTypingUsers } = useRoom();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSend = () => {
    if (!inputValue.trim() || !socket) return;

    socket.emit('chat:send', inputValue.trim());
    setInputValue('');

    // Stop typing indicator
    socket.emit('chat:stopped-typing');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);

    if (!socket) return;

    // Send typing indicator
    socket.emit('chat:typing');

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('chat:stopped-typing');
    }, 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const typingText = Array.from(chatTypingUsers.values()).join(', ');
  const inputContainerClassName = dockInputOnMobile
    ? 'fixed inset-x-0 bottom-0 z-40 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-line-soft bg-panel shadow-[0_-10px_28px_rgba(0,0,0,0.08)] lg:static lg:shadow-none lg:pb-3'
    : 'p-3 border-t border-line-soft';

  return (
    <div className="h-full min-w-0 flex flex-col overflow-hidden">
      {/* Messages */}
      <div className={`flex-1 min-h-0 overflow-y-auto p-4 space-y-3 ${dockInputOnMobile ? 'pb-24 lg:pb-4' : ''}`}>
        {chatMessages.length === 0 ? (
          <div className="text-center text-text-faint text-sm py-4">
            No messages yet
          </div>
        ) : (
          chatMessages.map((message) => (
            <div key={message.id} className="space-y-0.5">
              <div className="flex items-baseline gap-1.5">
                {message.profileId ? (
                  <Link
                    to={`/profile/${message.profileId}`}
                    className="text-[11.5px] font-semibold text-accent hover:underline"
                  >
                    {message.participantName}
                  </Link>
                ) : (
                  <span className="text-[11.5px] font-semibold text-accent">
                    {message.participantName}
                  </span>
                )}
                <span className="font-mono text-[9.5px] text-text-faint">
                  {new Date(message.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <p className="text-[12.5px] text-text leading-[1.45]">{message.content}</p>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {chatTypingUsers.size > 0 && (
        <div className="px-4 py-1 text-xs text-text-dim">
          {typingText} {chatTypingUsers.size === 1 ? 'is' : 'are'} typing...
        </div>
      )}

      {/* Input */}
      <div className={inputContainerClassName}>
        <div className="flex min-w-0 items-center gap-2 px-3 h-11 bg-bg-secondary border border-line rounded-lg focus-within:border-accent/45 focus-within:ring-[3px] focus-within:ring-accent/10 focus-within:bg-panel transition-all">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Reply..."
            className="min-w-0 flex-1 bg-transparent border-0 outline-none text-text text-[16px] leading-5 placeholder:text-text-faint lg:text-[12.5px]"
          />
          <span className="hidden sm:inline font-mono text-[9.5px] text-text-faint">Enter</span>
        </div>
      </div>
    </div>
  );
}
